// src/pages/monitoring/progress/MilestoneProgressUpdate.jsx
import React, { useState, useEffect } from 'react';
import { ArrowLeft, TrendingUp, CheckCircle, Clock, AlertCircle, Eye, Download, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import milestoneService from '../../../services/milestoneService';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Milestone Progress Update Component
 * 
 * Shows:
 * 1. Milestone overview (name, dates, current progress)
 * 2. Latest APPROVED progress submission
 * 3. Tab: Progress & Evidence
 *    - Evidence definitions (requirements)
 *    - Uploaded evidence files (LATEST STATUS per file)
 *    - Evidence grouped by definition
 *    - Status: APPROVED, PENDING, REJECTED, NOT_UPLOADED
 * 4. Tab: Issues & Tracking (placeholder)
 * 
 * ✅ FIX: milestoneData.milestoneTemplateId → milestoneData.templateId
 *    Backend field name is "templateId" (see ProjectMilestoneService.mapToResponse)
 * Evidence Display Logic:
 * - Show LATEST approval status per file
 * - Hide REJECTED files that were replaced with APPROVED
 * - One row per unique file showing END STATE only
 */
const MilestoneProgressUpdate = () => {
  const navigate = useNavigate();
  const { milestoneId } = useParams();
  
  // State
  const [milestone, setMilestone] = useState(null);
  const [latestApproved, setLatestApproved] = useState(null);
  const [evidenceDefinitions, setEvidenceDefinitions] = useState([]);
  const [uploadedEvidence, setUploadedEvidence] = useState([]);
  const [evidenceByDefinition, setEvidenceByDefinition] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('evidence');

  useEffect(() => {
    if (milestoneId) {
      fetchMilestoneData();
    }
  }, [milestoneId]);

  // Fetch all milestone data
  const fetchMilestoneData = async () => {
    setIsLoading(true);
    try {
      // 1. Get milestone detail
      const milestoneData = await milestoneService.getMilestoneById(milestoneId);
      setMilestone(milestoneData);
      
      // 2. Get all progress submissions for this milestone
      const progressList = await milestoneService.getProgressByMilestone(milestoneId);
      
      // Find latest APPROVED progress
      const approvedProgress = progressList
        .filter(p => p.status === 'APPROVED')
        .sort((a, b) => new Date(b.approvedAt) - new Date(a.approvedAt))[0];
      
      setLatestApproved(approvedProgress || null);
      
      // 3. Get evidence definitions (requirements)
       //
      // ✅ FIX: Gunakan `milestoneData.templateId` bukan `milestoneData.milestoneTemplateId`
      //
      // Backend ProjectMilestoneService.mapToResponse() mengembalikan field:
      //   .templateId(milestone.getMilestoneTemplate().getId())
      //
      // Bukan "milestoneTemplateId". Mengakses field yang tidak ada
      // menghasilkan JavaScript `undefined`, sehingga URL menjadi:
      //   GET /api/milestones/templates/undefined/evidence-definitions → 500 Error
      //
      // Selain itu: JANGAN gunakan state `milestone` di sini karena React
      // state update bersifat async — milestone masih null saat baris ini dieksekusi.
      // Selalu gunakan local variable `milestoneData`.
      //
      const templateId = milestoneData.templateId

      let definitions = [];

      if (templateId) {
        definitions = await milestoneService.getEvidenceDefinitions(templateId);
      } else {
        // Group milestones tidak punya templateId (isGroup=true)
        // Atau data belum lengkap — tidak perlu error
        console.warn('⚠️ Milestone templateId is null/undefined. Milestone isGroup?', milestoneData.isGroup, milestoneData);
      }

      setEvidenceDefinitions(definitions);
      
      // 4. Get ALL uploaded evidence files (from all progress submissions)
      // Aggregate from all progress submissions
      const allEvidence = progressList.flatMap(p => p.evidenceFiles || []);
      setUploadedEvidence(allEvidence);
      
      // 5. Process evidence for display
      const processed = processEvidenceForDisplay(definitions, allEvidence);
      setEvidenceByDefinition(processed);
      
      console.log('✅ Milestone data loaded:', {
        milestone: milestoneData, templateId,
        latestApproved: approvedProgress,
        definitions: definitions.length,
        evidence: allEvidence.length
      });
      
    } catch (error) {
      console.error('❌ Error loading milestone data:', error);
      toast.error('Failed to load milestone data');
    } finally {
      setIsLoading(false);
    }
  };

  // Process evidence for display - KEY LOGIC
  const processEvidenceForDisplay = (definitions, uploadedFiles) => {
    const result = {};
    
    definitions.forEach(definition => {
      // Get all files for this definition
      const filesForDefinition = uploadedFiles.filter(
        e => e.evidenceDefinitionId === definition.id
      );
      
      // Get latest state per file
      const latestFiles = getLatestStatePerFile(filesForDefinition);
      
      // Filter out REJECTED files that have been replaced
      const displayFiles = filterReplacedRejections(latestFiles);
      
      // Group by approval status
      const approved = displayFiles.filter(f => f.approvalStatus === 'APPROVED');
      const pending = displayFiles.filter(f => f.approvalStatus === 'PENDING');
      const rejected = displayFiles.filter(f => f.approvalStatus === 'REJECTED');
      
      // Calculate overall status
      const status = calculateDefinitionStatus(definition, approved, pending, rejected);
      
      result[definition.id] = {
        definition,
        files: displayFiles,
        approved,
        pending,
        rejected,
        status,
        count: displayFiles.length,
        requiredMin: definition.minFileCount || 1,
        requiredMax: definition.maxFileCount
      };
    });
    
    return result;
  };

  // Get latest state for each unique file
  const getLatestStatePerFile = (files) => {
    const grouped = files.reduce((acc, file) => {
      const key = file.originalFilename;
      if (!acc[key]) acc[key] = [];
      acc[key].push(file);
      return acc;
    }, {});
    
    return Object.values(grouped).map(fileVersions => {
      return fileVersions.sort((a, b) => 
        new Date(b.uploadedAt) - new Date(a.uploadedAt)
      )[0];
    });
  };

  // Remove REJECTED files that have been replaced with APPROVED
  const filterReplacedRejections = (files) => {
    const replacementMap = new Map();
    files.forEach(file => {
      if (file.isReplacement && file.replacedEvidenceId) {
        replacementMap.set(file.replacedEvidenceId, file);
      }
    });
    
    return files.filter(file => {
      if (file.approvalStatus === 'REJECTED') {
        const hasReplacement = Array.from(replacementMap.values())
          .some(replacement => 
            replacement.replacedEvidenceId === file.id &&
            replacement.approvalStatus === 'APPROVED'
          );
        return !hasReplacement;
      }
      return true;
    });
  };

  // Calculate overall status for evidence definition
  const calculateDefinitionStatus = (definition, approved, pending, rejected) => {
    const total = approved.length + pending.length + rejected.length;
    const minRequired = definition.minFileCount || 1;
    
    if (total === 0) {
      return {
        badge: 'NOT_UPLOADED',
        color: 'bg-gray-100 text-gray-700',
        icon: '⚪',
        label: definition.isMandatory ? 'Required - Not Uploaded' : 'Optional'
      };
    }
    
    if (approved.length >= minRequired) {
      return {
        badge: 'COMPLETE',
        color: 'bg-green-100 text-green-800',
        icon: '✅',
        label: `Complete (${approved.length}/${minRequired})`
      };
    }
    
    if (pending.length > 0) {
      return {
        badge: 'PENDING',
        color: 'bg-yellow-100 text-yellow-800',
        icon: '⏳',
        label: `Pending (${pending.length} awaiting)`
      };
    }
    
    if (rejected.length > 0) {
      return {
        badge: 'REJECTED',
        color: 'bg-red-100 text-red-800',
        icon: '❌',
        label: `Rejected (${rejected.length} need reupload)`
      };
    }
    
    return {
      badge: 'INCOMPLETE',
      color: 'bg-orange-100 text-orange-800',
      icon: '⚠️',
      label: `Incomplete (${approved.length}/${minRequired})`
    };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading || !milestone) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex space-x-2">
          <div className="w-3 h-3 bg-red-400 rounded-full animate-bounce"></div>
          <div className="w-3 h-3 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
          <div className="w-3 h-3 bg-red-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-800">Milestone Progress Detail</h1>
            <p className="text-gray-600 mt-1">
              {milestone.itemCode} - {milestone.milestoneName}
            </p>
          </div>
          <div className={`w-20 h-20 rounded-full flex items-center justify-center shadow-lg ${
            milestone.finalProgress >= 80 ? 'bg-green-500' :
            milestone.finalProgress >= 50 ? 'bg-yellow-500' : 'bg-red-500'
          }`}>
            <div className="text-center text-white">
              <div className="text-2xl font-bold">{(milestone.finalProgress || 0).toFixed(0)}%</div>
              <div className="text-xs">RAG</div>
            </div>
          </div>
        </div>
      </div>

      {/* Latest Approved Progress */}
      {latestApproved && (
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Latest Approved Progress
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs text-gray-500">Submitted By</label>
              <p className="font-semibold">{latestApproved.submittedByName}</p>
              <p className="text-xs text-gray-500">{formatDate(latestApproved.submittedAt)}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500">Progress</label>
              <p className="font-semibold text-2xl text-green-600">
                {(latestApproved.approvedProgress || 0).toFixed(1)}%
              </p>
            </div>
            <div>
              <label className="text-xs text-gray-500">Approved By</label>
              <p className="font-semibold">{latestApproved.approvedByName}</p>
              <p className="text-xs text-gray-500">{formatDate(latestApproved.approvedAt)}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500">Evidence</label>
              <p className="font-semibold">{latestApproved.evidenceApprovedCount || 0} approved</p>
              <p className="text-xs text-gray-500">of {latestApproved.evidenceCount || 0} total</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="border-b border-gray-200">
          <div className="flex px-6">
            <button
              onClick={() => setActiveTab('evidence')}
              className={`px-6 py-4 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'evidence'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              Progress & Evidence
            </button>
            <button
              onClick={() => setActiveTab('issues')}
              className={`px-6 py-4 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'issues'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              Issues & Tracking
            </button>
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'evidence' && (
            <div className="space-y-6">
              {evidenceDefinitions.length === 0 ? (
                <div className="text-center py-12">
                  <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No evidence requirements defined</p>
                </div>
              ) : (
                evidenceDefinitions.map(definition => {
                  const evidenceData = evidenceByDefinition[definition.id] || {
                    files: [],
                    approved: [],
                    pending: [],
                    rejected: [],
                    status: {
                      badge: 'NOT_UPLOADED',
                      color: 'bg-gray-100 text-gray-700',
                      icon: '⚪',
                      label: 'Not Uploaded'
                    }
                  };

                  return (
                    <div key={definition.id} className="border border-gray-200 rounded-lg p-4">
                      {/* Evidence Definition Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 flex items-center gap-2">
                            {definition.evidenceName}
                            {definition.isMandatory && (
                              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                                MANDATORY
                              </span>
                            )}
                          </h4>
                          <p className="text-xs text-gray-600 mt-1">
                            Type: {definition.evidenceType} | 
                            Required: {definition.minFileCount || 1}-{definition.maxFileCount || '∞'} files
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${evidenceData.status.color}`}>
                          {evidenceData.status.icon} {evidenceData.status.label}
                        </span>
                      </div>

                      {/* Uploaded Files */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {evidenceData.approved.map(file => (
                          <EvidenceFileCard key={file.id} file={file} status="APPROVED" />
                        ))}
                        {evidenceData.pending.map(file => (
                          <EvidenceFileCard key={file.id} file={file} status="PENDING" />
                        ))}
                        {evidenceData.rejected.map(file => (
                          <EvidenceFileCard key={file.id} file={file} status="REJECTED" />
                        ))}
                        
                        {/* Upload Placeholder */}
                        {evidenceData.count < (definition.maxFileCount || 999) && (
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center hover:border-blue-500 transition-colors cursor-pointer">
                            <Upload className="w-8 h-8 text-gray-400 mb-2" />
                            <p className="text-xs text-gray-500">Upload More</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {activeTab === 'issues' && (
            <div className="text-center py-12">
              <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">Issues Tracking - Coming Soon</p>
              <p className="text-xs text-gray-400 mt-2">This feature is under development</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Evidence File Card Component
const EvidenceFileCard = ({ file, status }) => {
  const statusConfig = {
    APPROVED: { color: 'border-green-500 bg-green-50', badge: 'bg-green-100 text-green-800', icon: '✅' },
    PENDING: { color: 'border-yellow-500 bg-yellow-50', badge: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
    REJECTED: { color: 'border-red-500 bg-red-50', badge: 'bg-red-100 text-red-800', icon: '❌' }
  };

  const config = statusConfig[status];

  return (
    <div className={`border-2 rounded-lg p-3 ${config.color}`}>
      {file.thumbnailUrl && file.evidenceType === 'PHOTO' ? (
        <img 
          src={file.thumbnailUrl} 
          alt={file.originalFilename}
          className="w-full h-32 object-cover rounded mb-2"
        />
      ) : (
        <div className="w-full h-32 bg-gray-200 rounded mb-2 flex items-center justify-center">
          <span className="text-4xl">📄</span>
        </div>
      )}
      
      <p className="font-semibold text-xs text-gray-900 truncate" title={file.originalFilename}>
        {file.originalFilename}
      </p>
      
      <span className={`inline-block mt-2 px-2 py-1 rounded-full text-xs font-semibold ${config.badge}`}>
        {config.icon} {status}
      </span>
      
      <div className="mt-2 text-xs text-gray-600">
        <p>By: {file.uploadedByName}</p>
        <p>{new Date(file.uploadedAt).toLocaleDateString('id-ID')}</p>
        {status === 'APPROVED' && file.reviewedByName && (
          <p className="text-green-700 font-medium">Approved by: {file.reviewedByName}</p>
        )}
        {status === 'REJECTED' && (
          <p className="text-red-700 mt-1">{file.rejectionReason}</p>
        )}
      </div>
      
      <div className="mt-3 flex gap-2">
        <button className="flex-1 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700">
          <Eye className="w-3 h-3 inline mr-1" />
          View
        </button>
        <button className="flex-1 px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700">
          <Download className="w-3 h-3 inline mr-1" />
          Download
        </button>
      </div>
    </div>
  );
};

export default MilestoneProgressUpdate;